/**
 * JWT Token Auto-Refresh Manager
 * 
 * Automatically refreshes JWT token when it's about to expire
 * Runs in background and checks token expiry every hour
 */

import authService from '../services/api/auth.service';
import { useAuthStore } from '../store/authStore';

interface JWTPayload {
  iss: string;      // Issuer
  iat: number;      // Issued at
  exp: number;      // Expiry
  sub: number;      // Subject (user ID)
  email: string;    // User email
  jti: string;      // JWT ID
  device: string;   // Device fingerprint
}

class TokenRefreshManager {
  private refreshInterval: NodeJS.Timeout | null = null;
  private checkInterval: number = 60 * 60 * 1000; // Check every hour
  private refreshThreshold: number = 5 * 60 * 1000; // Refresh if less than 5 minutes remaining

  /**
   * Start auto-refresh service
   */
  start(): void {
    if (this.refreshInterval) {

      return;
    }

// Initial check
    this.checkAndRefreshToken();
    
    // Set up periodic checks
    this.refreshInterval = setInterval(() => {
      this.checkAndRefreshToken();
    }, this.checkInterval);

}

  /**
   * Stop auto-refresh service
   */
  stop(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;

    }
  }

  /**
   * Check token expiry and refresh if needed
   */
  private async checkAndRefreshToken(): Promise<void> {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        // No token, ensure app state is logged out (covers stale persisted store)
        const state = useAuthStore.getState();
        if (state?.isAuthenticated || state?.token) {
          state.logout();
        }
        return;
      }

      // Decode JWT to get expiry time
      const payload = this.decodeJWT(token);
      
      if (!payload || !payload.exp) {
        // Broken token format => logout
        useAuthStore.getState().logout();
        return;
      }

      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeRemaining = expiresAt - now;
      // If less than 1 day remaining, refresh token
      if (timeRemaining < this.refreshThreshold && timeRemaining > 0) {

        const refreshed = await authService.refreshToken().catch(() => ({ success: false } as any));
        // Only force logout if token has actually expired.
        // If refresh fails due to network/proxy/endpoint issues, keep the session until a real auth error occurs.
        const stillValid = timeRemaining > 0;
        if (!refreshed?.success && !stillValid) {
          useAuthStore.getState().logout();
        }

      } else if (timeRemaining <= 0) {

        // Expired token => force logout so UI doesn't think user is still logged in
        useAuthStore.getState().logout();
      }
    } catch (error) {
      // Don't force logout on unexpected errors; avoid kicking users due to transient issues.
    }
  }

  /**
   * Decode JWT token (without verification)
   * Only used to read expiry time
   */
  private decodeJWT(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      
      if (parts.length !== 3) {

        return null;
      }

      // Get payload (second part)
      const payload = parts[1];
      
      // Decode base64url
      const decoded = this.base64urlDecode(payload);
      
      // Parse JSON
      return JSON.parse(decoded) as JWTPayload;
    } catch (error) {

      return null;
    }
  }

  /**
   * Base64 URL decode
   */
  private base64urlDecode(str: string): string {
    // Replace URL-safe characters
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }
    
    // Decode base64
    return atob(base64);
  }

  /**
   * Get token expiry info
   */
  getTokenInfo(): { 
    isValid: boolean; 
    expiresAt: Date | null; 
    timeRemaining: number | null;
    userID: number | null;
  } {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      return {
        isValid: false,
        expiresAt: null,
        timeRemaining: null,
        userID: null
      };
    }

    const payload = this.decodeJWT(token);
    
    if (!payload) {
      return {
        isValid: false,
        expiresAt: null,
        timeRemaining: null,
        userID: null
      };
    }

    const expiresAt = new Date(payload.exp * 1000);
    const timeRemaining = payload.exp * 1000 - Date.now();

    return {
      isValid: timeRemaining > 0,
      expiresAt,
      timeRemaining: Math.max(0, timeRemaining),
      userID: payload.sub
    };
  }

  /**
   * Force immediate token refresh
   */
  async forceRefresh(): Promise<boolean> {
    try {

      const result = await authService.refreshToken();
      return result.success;
    } catch (error) {

      return false;
    }
  }
}

// Create singleton instance
const tokenRefreshManager = new TokenRefreshManager();

// Auto-start on import (can be disabled if needed)
if (typeof window !== 'undefined') {
  // Wait a bit before starting to avoid race conditions
  setTimeout(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      tokenRefreshManager.start();
    }
  }, 1000);
}

export default tokenRefreshManager;
